// Comment list component for post detail page

import { useComments } from '@/hooks/useComments'
import { CommentItem } from './CommentItem'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { MessageCircle, RefreshCw } from 'lucide-react'

interface CommentListProps {
  author: string
  permlink: string
  commentCount?: number
}

export function CommentList({ author, permlink, commentCount }: CommentListProps) {
  const { comments, isLoading, isError, refetch } = useComments({
    author,
    permlink,
    enabled: true,
    includeReplies: true,
    maxDepth: 3,
  })

  if (isLoading) {
    return (
      <div className="mt-8">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <MessageCircle className="h-5 w-5" />
          댓글
        </h2>
        <CommentListSkeleton />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="mt-8">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <MessageCircle className="h-5 w-5" />
          댓글
        </h2>
        <div className="text-center py-8 text-muted-foreground">
          <p className="mb-4">댓글을 불러오는데 실패했습니다</p>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            다시 시도
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="mt-8">
      <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <MessageCircle className="h-5 w-5" />
        댓글 {commentCount !== undefined && `(${commentCount})`}
      </h2>

      {comments.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <p>아직 댓글이 없습니다</p>
        </div>
      ) : (
        <div className="divide-y">
          {comments.map((comment) => (
            <CommentItem
              key={`${comment.author}-${comment.permlink}`}
              comment={comment}
              showReplies={true}
              rootAuthor={author}
              rootPermlink={permlink}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function CommentListSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-start gap-3 py-4">
          <Skeleton className="h-8 w-8 rounded-full" />
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-3 w-12" />
            </div>
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <div className="flex gap-4">
              <Skeleton className="h-3 w-8" />
              <Skeleton className="h-3 w-8" />
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

export { CommentItem }
